import React from 'react';
import StyleSheet from "react-native-web/dist/exports/StyleSheet";
import Text from "react-native-web/dist/exports/Text";
import View from "react-native-web/dist/exports/View";
import PropTypes from 'prop-types';
import { applyTextStyles, useTheme, useThemeTokens } from '../ThemeProvider';
import { a11yProps, getTokensPropType, selectSystemProps, selectTokens, variantProp, viewProps } from '../utils';
import StackView from '../StackView';
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
const [selectProps, selectedSystemPropTypes] = selectSystemProps([a11yProps, viewProps]);
const selectStyles = tokens => selectTokens('Feedback', tokens);
const selectTitleTextStyles = (_ref, themeOptions) => {
  let {
    titleFontSize,
    ...tokens
  } = _ref;
  return applyTextStyles({
    ...selectTokens('Typography', {
      ...tokens,
      fontSize: titleFontSize,
      themeOptions
    }),
    themeOptions
  });
};
const selectContentTextStyles = (_ref2, themeOptions) => {
  let {
    contentFontSize,
    ...tokens
  } = _ref2;
  return applyTextStyles({
    ...selectTokens('Typography', {
      ...tokens,
      fontSize: contentFontSize
    }),
    themeOptions
  });
};
const selectIconTokens = _ref3 => {
  let {
    iconSize,
    iconColor
  } = _ref3;
  return {
    size: iconSize,
    color: iconColor
  };
};
const selectIconContainerStyles = _ref4 => {
  let {
    iconGap
  } = _ref4;
  return {
    paddingRight: iconGap
  };
};

/**
 * A feedback box commonly used with form fields.
 *
 * ### Standalone usage
 * While its primary use is to facilitate feedback states for other form components such as `TextInput`,
 * you may use it standalone.
 *
 * ### Complex content
 * You may pass any React tree as the children of this component, bear in mind that a render function
 * is better suited for styling children based on Feedback's variant.
 *
 * ### Using a render function
 * When a function is passed for rendering content, it will receive the feedback text styles and
 * variant as arguments.
 *
 * ### Accessibility
 * All accessibility props set on this component will be applied to the outer container.
 */
const Feedback = /*#__PURE__*/React.forwardRef((_ref5, ref) => {
  let {
    title,
    children,
    id,
    validation,
    tokens,
    variant,
    ...rest
  } = _ref5;
  const themeTokens = useThemeTokens('Feedback', tokens, {
    ...variant,
    validation
  });
  const {
    space
  } = themeTokens;
  const {
    icon: IconComponent
  } = themeTokens;
  const {
    themeOptions
  } = useTheme();
  const titleTextStyles = selectTitleTextStyles(themeTokens, themeOptions);
  const contentTextStyles = selectContentTextStyles(themeTokens, themeOptions);
  const content = typeof children === 'string' ? /*#__PURE__*/_jsx(Text, {
    style: contentTextStyles,
    children: children
  }) : children;
  const selectedProps = selectProps({
    accessibilityRole: validation === 'error' ? 'alert' : undefined,
    ...rest
  });
  return /*#__PURE__*/_jsx(View, {
    ref: ref,
    style: selectStyles(themeTokens),
    nativeID: id,
    ...selectedProps,
    children: /*#__PURE__*/_jsxs(StackView, {
      space: space,
      children: [title !== undefined && /*#__PURE__*/_jsxs(View, {
        style: staticStyles.title,
        children: [IconComponent && /*#__PURE__*/_jsx(View, {
          style: selectIconContainerStyles(themeTokens),
          children: /*#__PURE__*/_jsx(IconComponent, {
            ...selectIconTokens(themeTokens)
          })
        }), /*#__PURE__*/_jsx(Text, {
          style: titleTextStyles,
          children: title
        })]
      }), content && typeof content === 'function' ? content({
        textStyles: contentTextStyles,
        variant
      }) : content]
    })
  });
});
Feedback.displayName = 'Feedback';
Feedback.propTypes = {
  ...selectedSystemPropTypes,
  /**
   * Emphasized summary of the feedback. If an icon is set, it is rendered next to the title.
   */
  title: PropTypes.string,
  /**
   * Feedback content rendered below the title. A render function `({textStyles, variant}) => {}` is supported.
   */
  children: PropTypes.oneOfType([PropTypes.string, PropTypes.node, PropTypes.func]),
  /**
   * Use to visually mark the Feedback as valid or invalid.
   */
  validation: PropTypes.oneOf(['error', 'success']),
  /** @ignore */
  id: PropTypes.string,
  tokens: getTokensPropType('Feedback'),
  variant: variantProp.propType
};
export default Feedback;
const staticStyles = StyleSheet.create({
  title: {
    display: 'flex',
    flexDirection: 'row'
  }
});